import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Award,
  Sparkles,
  Zap,
  Sliders,
  Sun,
  Activity,
  Heart,
  Moon,
  ArrowRight,
  CheckCircle2,
  Tv,
  Wifi,
  Radio,
  SlidersHorizontal
} from 'lucide-react';

const AboutUs = () => {
  const heroBadges = [
    { label: 'Clinically Inspired', icon: ShieldCheck },
    { label: 'Non-Invasive & Safe', icon: Award },
    { label: 'Premium Quality', icon: Sparkles },
    { label: 'Trusted Support', icon: CheckCircle2 }
  ];

  const differenceFeatures = [
    {
      title: 'HIGH-OUTPUT LEDs',
      desc: 'High irradiance LEDs designed for powerful and effective light delivery.',
      icon: Sun
    },
    {
      title: '660nm + 850nm',
      desc: 'Dual wavelength technology combining red and near-infrared light for optimal results.',
      icon: Activity
    },
    {
      title: 'ADJUSTABLE CONTROL',
      desc: 'Individual wavelength adjustment and dimming capabilities from 1% to 100%.',
      icon: Sliders
    }
  ];

  const realLifeCards = [
    {
      title: 'FITNESS & ATHLETES',
      desc: 'Support faster recovery, reduce muscle fatigue and improve performance naturally.',
      img: '/panel_144led_1787122630966.jpg',
      icon: Activity
    },
    {
      title: 'PAIN MANAGEMENT',
      desc: 'Help reduce discomfort and inflammation associated with daily aches and pain.',
      img: '/hero_person_red_light_session_1787202717236.jpg',
      icon: Heart
    },
    {
      title: 'SKIN HEALTH & ANTI-AGING',
      desc: 'Support collagen production, improve skin tone, texture and overall skin health.',
      img: '/journal_skin_1787124316808.jpg',
      icon: Sparkles
    },
    {
      title: 'WELLNESS & RECOVERY',
      desc: 'Improve sleep quality, boost mood and support overall wellbeing every day.',
      img: '/journal_sleep_1787124351087.jpg',
      icon: Moon
    }
  ];

  const panelDifferences = [
    { title: '1–100% DIMMING', desc: 'Adjust intensity from 1% to 100%', icon: SlidersHorizontal },
    { title: 'INDIVIDUAL WAVELENGTH CONTROL', desc: 'Adjust 660nm and 850nm independently', icon: Sliders },
    { title: 'TOUCH SCREEN', desc: 'Easy to use smart touch control', icon: Tv },
    { title: 'FLICKER SUPPORT', desc: 'Flicker frequencies 1 – 10,000Hz', icon: Radio },
    { title: 'DUAL-CHIP LEDs', desc: 'High-performance and long lasting', icon: Zap },
    { title: '2.4G REMOTE SUPPORT', desc: 'Control your panel distance', icon: Wifi }
  ];

  const panelNeeds = [
    { title: 'Targeted', leds: '72 – 144 LEDs' },
    { title: 'Small Area', leds: '144 – 288 LEDs' },
    { title: 'Half Body', leds: '288 – 576 LEDs' },
    { title: 'Full Body', leds: '720 – 864 LEDs' },
    { title: 'Professional', leds: '1,152 LEDs+' }
  ];

  const productRange = [
    { title: '72 LEDs', sub: 'Targeted' },
    { title: '144 LEDs', sub: 'Targeted' },
    { title: '216 LEDs', sub: 'Small Area' },
    { title: '288 LEDs', sub: 'Half Body' },
    { title: '432 LEDs', sub: 'Half Body' },
    { title: '576 LEDs', sub: 'Full Body' },
    { title: '720 LEDs', sub: 'Full Body' },
    { title: '864 LEDs', sub: 'Full Body' },
    { title: '1,152 LEDs', sub: 'Professional' }
  ];

  const ourPromises = [
    {
      title: 'PREMIUM QUALITY & DURABILITY',
      desc: 'Built with high-quality materials and premium components for long-term performance.',
      icon: ShieldCheck
    },
    {
      title: 'HIGH OUTPUT LEDs 660nm & 850nm',
      desc: 'Powerful dual-wavelength LEDs for effective light therapy results.',
      icon: Sun
    },
    {
      title: 'SAFE, EFFECTIVE & NON-INVASIVE',
      desc: 'A natural way to support your health, recovery and overall wellness.',
      icon: Heart
    },
    {
      title: 'DEDICATED CUSTOMER SUPPORT',
      desc: "We're here to help you get the most out of your InfraEdge experience.",
      icon: CheckCircle2
    }
  ];

  return (
    <div style={{ background: '#ffffff', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a' }}>
      
      {/* 1. HERO BANNER - BETTER LIGHT. BETTER WELLNESS. */}
      <section style={{ background: '#ffffff', color: '#0f172a', position: 'relative', overflow: 'hidden', padding: '60px 24px 80px', borderBottom: '1px solid #f1f5f9', width: '100%', boxSizing: 'border-box' }}>
        
        {/* Generated White Background with Red Light Laser Beams & Waves Overlay (Same as Home Banner) */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `linear-gradient(to right, rgba(255, 255, 255, 0.94) 25%, rgba(255, 255, 255, 0.65) 55%, rgba(255, 255, 255, 0.82) 100%), url('/hero_white_laser_bg_1787202417710.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          backgroundRepeat: 'no-repeat',
          opacity: 0.95,
          pointerEvents: 'none',
          zIndex: 1
        }}></div>

        {/* Red Light Ambient Radial Glow on Right Side */}
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(239, 68, 68, 0.22) 0%, rgba(220, 38, 38, 0.05) 50%, transparent 80%)', filter: 'blur(60px)', pointerEvents: 'none', zIndex: 1 }}></div>

        <div style={{ maxWidth: '1240px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center', position: 'relative', zIndex: 2, width: '100%', boxSizing: 'border-box' }}>
          
          {/* Left Hero Story Content */}
          <div style={{ width: '100%', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '16px', display: 'block' }}>
              OUR STORY
            </span>
            <h1 style={{ fontSize: '50px', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, marginBottom: '24px', letterSpacing: '-0.03em', wordBreak: 'break-word' }}>
              Better Light.<br />
              Better <span style={{ color: '#dc2626' }}>Wellness</span>.
            </h1>
            <p style={{ fontSize: '15.5px', color: '#475569', lineHeight: 1.7, marginBottom: '40px', maxWidth: '100%' }}>
              InfraEdge Red Light Therapy is built around advanced red and near-infrared light technology designed to support recovery, skin health, pain management and overall wellness.
            </p>

            {/* 4 Feature Badges Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '14px', width: '100%', boxSizing: 'border-box' }}>
              {heroBadges.map((b, i) => (
                <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '16px 10px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', boxSizing: 'border-box' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                    <b.icon size={18} />
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', lineHeight: 1.3, display: 'block', wordBreak: 'break-word' }}>{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Hero Dark Glossy Graphic */}
          <div style={{ textAlign: 'center', width: '100%', boxSizing: 'border-box' }}>
            <img
              src="/about_hero_dark_panel.jpg"
              alt="InfraEdge Red Light Therapy Panel Banner"
              style={{ width: '100%', maxWidth: '100%', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', display: 'block' }}
            />
          </div>

        </div>
      </section>

      {/* 2. THE INFRAEDGE DIFFERENCE (3 CARDS) */}
      <section style={{ padding: '70px 24px', background: '#fafbfc', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              THE INFRAEDGE DIFFERENCE
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px' }}>
            {differenceFeatures.map((feat, i) => (
              <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px 24px', textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#dc2626', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', boxShadow: '0 6px 18px rgba(220,38,38,0.25)' }}>
                  <feat.icon size={26} />
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {feat.title}
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                  {feat.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. TECHNOLOGY THAT FITS INTO REAL LIFE (4 LIFESTYLE CARDS) */}
      <section style={{ padding: '80px 24px', background: '#ffffff' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              TECHNOLOGY THAT FITS INTO REAL LIFE
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' }}>
            {realLifeCards.map((card, i) => (
              <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <div style={{ position: 'relative', height: '180px' }}>
                  <img src={card.img} alt={card.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', bottom: '-20px', left: '20px', width: '44px', height: '44px', borderRadius: '50%', background: '#ffffff', border: '2px solid #dc2626', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    <card.icon size={20} />
                  </div>
                </div>

                <div style={{ padding: '36px 20px 24px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                    {card.title}
                  </h3>
                  <p style={{ fontSize: '12.5px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                    {card.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. WHY 660nm + 850nm? (SCIENCE WAVELENGTH COMPARISON) */}
      <section style={{ padding: '80px 24px', background: '#fafbfc', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '32px', alignItems: 'center' }}>
          
          {/* Column 1: Left Text */}
          <div>
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '10px', display: 'block' }}>
              THE SCIENCE BEHIND THE LIGHT
            </span>
            <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', lineHeight: 1.2, marginBottom: '16px' }}>
              Why 660nm + 850nm?
            </h2>
            <p style={{ fontSize: '13.5px', color: '#64748b', lineHeight: 1.6, marginBottom: '24px' }}>
              Our carefully selected wavelengths work at different depths to maximize the benefits of red light therapy.
            </p>
            <Link to="/science" style={{ border: '1px solid #dc2626', color: '#dc2626', padding: '10px 20px', borderRadius: '9999px', fontSize: '12.5px', fontWeight: 800, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <span>Explore The Science</span>
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Column 2: 660nm Red Light Box */}
          <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '16px', padding: '24px' }}>
            <div style={{ fontSize: '15px', fontWeight: 900, color: '#dc2626', marginBottom: '16px' }}>
              660nm <span style={{ fontSize: '12px', fontWeight: 700 }}>Red Light</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {['Targets the skin', 'Supports collagen production', 'Enhances skin health and appearance', 'Helps reduce inflammation'].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <CheckCircle2 size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Column 3: Skin Penetration Depth Illustration */}
          <div style={{ textAlign: 'center' }}>
            <img src="/skin_penetration_depth_1787559216637.jpg" alt="660nm and 850nm skin penetration depth" style={{ width: '100%', borderRadius: '16px', border: '1px solid #e2e8f0' }} />
          </div>

          {/* Column 4: 850nm Near-Infrared Box */}
          <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '16px', padding: '24px' }}>
            <div style={{ fontSize: '15px', fontWeight: 900, color: '#b91c1c', marginBottom: '16px' }}>
              850nm <span style={{ fontSize: '12px', fontWeight: 700 }}>Near-Infrared Light</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {['Penetrates deeper into tissues', 'Supports muscle recovery', 'Promotes cellular energy (ATP)', 'Helps reduce pain and stiffness'].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <CheckCircle2 size={16} color="#b91c1c" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 5. WHAT MAKES OUR PANELS DIFFERENT (6 ICON GRID) */}
      <section style={{ padding: '80px 24px', background: '#ffffff' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', letterSpacing: '0.04em' }}>
              What Makes Our Panels Different
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '20px' }}>
            {panelDifferences.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} style={{ background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px 16px', textAlign: 'center' }}>
                  <div style={{ width: '40px', height: '40px', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <Icon size={24} />
                  </div>
                  <h3 style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', marginBottom: '6px', textTransform: 'uppercase' }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '11.5px', color: '#64748b', margin: 0, lineHeight: 1.4 }}>
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. ONE TECHNOLOGY. DIFFERENT NEEDS. (PANEL SIZES GUIDE) */}
      <section style={{ padding: '80px 24px', background: '#fafbfc', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '48px', alignItems: 'center', marginBottom: '48px' }}>
            <div>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>
                ONE TECHNOLOGY. DIFFERENT NEEDS.
              </span>
              <p style={{ fontSize: '13.5px', color: '#64748b', lineHeight: 1.6, marginBottom: '24px' }}>
                Whether you're just getting started or need full-body coverage, we have the perfect panel for you.
              </p>
              <Link to="/shop" style={{ border: '1px solid #dc2626', color: '#dc2626', padding: '10px 20px', borderRadius: '9999px', fontSize: '12.5px', fontWeight: 800, textDecoration: 'none', display: 'inline-block' }}>
                View All Panels
              </Link>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '16px' }}>
              {panelNeeds.map((need, idx) => (
                <div key={idx} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', marginBottom: '4px' }}>{need.title}</div>
                  <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>{need.leds}</div>
                </div>
              ))}
            </div>
          </div>

          {/* OUR PRODUCT RANGE STEPPERS ROW */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              OUR PRODUCT RANGE
            </h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px' }}>
            {productRange.map((p, i) => (
              <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', fontWeight: 900, color: '#dc2626', marginBottom: '2px' }}>{p.title}</div>
                <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 700 }}>{p.sub}</div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 7. BUILT TO PERFORM. BUILT TO LAST. (QUALITY & COMPLIANCE) */}
      <section style={{ padding: '80px 24px', background: '#ffffff' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '40px', alignItems: 'center' }}>
          
          {/* Left Features List */}
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', marginBottom: '16px', lineHeight: 1.3 }}>
              BUILT TO PERFORM.<br />BUILT TO LAST.
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                'Iron Shell – Durable & Protective',
                'Premium Components – Reliable & Long Lasting',
                'Advanced Cooling – Quiet & Efficient',
                'Adjustable Performance – 1% to 100% Dimming',
                'Designed for Safety & Daily Use'
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <CheckCircle2 size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ fontSize: '12.5px', color: '#475569', fontWeight: 600 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>


          {/* Right Quality & Compliance */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px', textAlign: 'center' }}>
            <h4 style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', marginBottom: '8px' }}>
              QUALITY & COMPLIANCE
            </h4>
            <p style={{ fontSize: '11.5px', color: '#64748b', lineHeight: 1.5, marginBottom: '20px' }}>
              InfraEdge panels are designed to meet high standards of quality and safety.
            </p>
            <div style={{ fontSize: '24px', fontWeight: 900, color: '#0f172a', letterSpacing: '0.2em' }}>
              UK / CA • CE
            </div>
          </div>

        </div>
      </section>

      {/* 8. OUR PROMISE (4 COLUMNS) */}
      <section style={{ padding: '60px 24px 80px', background: '#fafbfc', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              OUR PROMISE
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' }}>
            {ourPromises.map((p, i) => (
              <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                  <p.icon size={20} />
                </div>
                <h3 style={{ fontSize: '13.5px', fontWeight: 900, color: '#0f172a', marginBottom: '8px', textTransform: 'uppercase' }}>
                  {p.title}
                </h3>
                <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                  {p.desc}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 9. BOTTOM CALLOUT BANNER - HEAL SMARTER. LIVE BETTER. */}
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '24px', padding: '40px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '32px' }}>
          <div style={{ maxWidth: '580px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', lineHeight: 1.2, marginBottom: '10px' }}>
              Heal Smarter. Live Better.
            </h2>
            <p style={{ fontSize: '14.5px', color: '#475569', lineHeight: 1.6, margin: 0 }}>
              Discover the InfraEdge range and find the panel that fits your wellness goals.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Link to="/shop" style={{ background: '#dc2626', color: '#ffffff', padding: '14px 28px', borderRadius: '9999px', fontSize: '13.5px', fontWeight: 800, textDecoration: 'none', boxShadow: '0 8px 20px rgba(220, 38, 38, 0.25)' }}>
              Explore Our Panels
            </Link>
            <Link to="/science" style={{ border: '1px solid #dc2626', color: '#dc2626', background: '#ffffff', padding: '14px 28px', borderRadius: '9999px', fontSize: '13.5px', fontWeight: 800, textDecoration: 'none' }}>
              Explore The Science
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;
